/**
 * Subscription Dashboard Component
 * Shows current subscription status and options
 */

import { useState } from 'react';
import type { UserSubscription } from '@/types/subscription';

interface SubscriptionDashboardProps {
  subscription: UserSubscription;
  onUpgrade?: () => void;
  onDowngrade?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

export function SubscriptionDashboard({
  subscription,
  onUpgrade,
  onDowngrade,
  onCancel,
  isLoading = false,
}: SubscriptionDashboardProps) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const getPlanIcon = (code: string) => {
    const icons: Record<string, string> = {
      free: '🎸',
      group: '🎼',
      unlimited: '🎵',
    };
    return icons[code] || '🎵';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'text-green-400',
      cancelled: 'text-red-400',
      expired: 'text-yellow-400',
    };
    return colors[status] || 'text-white/50';
  };

  return (
    <div className="bg-[#111111] border border-white/5 rounded-2xl p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            {getPlanIcon(subscription.plan_detail.code)} {subscription.plan_detail.name}
          </h2>
          <p className={`text-sm font-semibold ${getStatusColor(subscription.status)}`}>
            {subscription.status_display}
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-white">
            {subscription.plan_detail.code === 'free' ? 'Grátis' : subscription.price_paid_brl}
          </div>
          {subscription.plan_detail.code !== 'free' && (
            <p className="text-xs text-white/40 mt-1">/mês</p>
          )}
        </div>
      </div>

      {/* Subscription Info Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8 pb-8 border-b border-white/5">
        <div>
          <p className="text-xs text-white/40 uppercase mb-1">Ativo desde</p>
          <p className="text-sm font-medium text-white">
            {new Date(subscription.started_at).toLocaleDateString('pt-BR')}
          </p>
        </div>
        <div>
          <p className="text-xs text-white/40 uppercase mb-1">Renovação</p>
          <p className="text-sm font-medium text-white">
            {subscription.plan_detail.code === 'free'
              ? '—'
              : new Date(subscription.renews_at).toLocaleDateString('pt-BR')}
          </p>
        </div>
        <div>
          <p className="text-xs text-white/40 uppercase mb-1">Dias restantes</p>
          {subscription.plan_detail.code === 'free' ? (
            <p className="text-sm font-medium text-white/50">tempo indeterminado</p>
          ) : (
            <p className="text-sm font-medium text-[#C8A96E]">
              {subscription.remaining_days} dias
            </p>
          )}
        </div>
      </div>

      {/* Coupon Info */}
      {subscription.coupon_detail && (
        <div className="mb-8 space-y-3">
          <div className="bg-[#C8A96E]/10 border border-[#C8A96E]/20 rounded-lg p-4">
            <p className="text-xs text-[#C8A96E] uppercase font-semibold mb-1">
              Cupom Ativo
            </p>
            <p className="text-sm text-white">
              {subscription.coupon_detail.code} -{' '}
              {subscription.coupon_detail.discount_type === 'percentage'
                ? `${subscription.coupon_detail.discount_value}% desconto`
                : `${subscription.coupon_detail.discount_value} mês(es) grátis`}
            </p>
          </div>
          {subscription.coupon_expires_at && (
            <div className={`border rounded-lg p-4 flex items-center gap-3 ${
              (subscription.coupon_remaining_days ?? 0) <= 7
                ? 'bg-red-500/10 border-red-500/30'
                : 'bg-yellow-500/10 border-yellow-500/30'
            }`}>
              <span className="text-lg">⏳</span>
              <div>
                <p className={`text-xs font-semibold uppercase mb-0.5 ${
                  (subscription.coupon_remaining_days ?? 0) <= 7 ? 'text-red-400' : 'text-yellow-400'
                }`}>
                  Acesso via cupom
                </p>
                <p className="text-sm text-white">
                  Seu acesso ao plano <strong>{subscription.plan_detail.name}</strong> expira em{' '}
                  {subscription.coupon_remaining_days != null && subscription.coupon_remaining_days > 0
                    ? <><strong>{subscription.coupon_remaining_days} dia{subscription.coupon_remaining_days !== 1 ? 's' : ''}</strong>{' '}({new Date(subscription.coupon_expires_at).toLocaleDateString('pt-BR')})</>
                    : <strong className="text-red-400">hoje</strong>
                  }. Após essa data você será movido para o plano Free.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {subscription.status === 'active' && (
        <div className="flex flex-col sm:flex-row gap-3">
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              disabled={isLoading}
              className="flex-1 py-3 px-4 bg-[#C8A96E] text-[#0A0A0F] rounded-lg font-semibold hover:bg-[#D4B882] disabled:opacity-50 transition-all"
            >
              {isLoading ? 'Processando...' : '⬆️ Fazer Upgrade'}
            </button>
          )}
          {onDowngrade && (
            <button
              onClick={onDowngrade}
              disabled={isLoading}
              className="flex-1 py-3 px-4 border border-white/20 text-white rounded-lg font-semibold hover:border-white/40 hover:bg-white/5 disabled:opacity-50 transition-all"
            >
              {isLoading ? 'Processando...' : '⬇️ Fazer Downgrade'}
            </button>
          )}
          {onCancel && (
            <>
              <button
                onClick={() => setShowCancelConfirm(true)}
                disabled={isLoading}
                className="flex-1 py-3 px-4 border border-red-500/20 text-red-400 rounded-lg font-semibold hover:border-red-500/40 hover:bg-red-500/5 disabled:opacity-50 transition-all"
              >
                {isLoading ? 'Processando...' : '❌ Cancelar'}
              </button>

              {showCancelConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 max-w-sm">
                    <h3 className="text-xl font-bold text-white mb-3">
                      Cancelar Assinatura?
                    </h3>
                    <p className="text-white/60 text-sm mb-6">
                      Você perderá acesso a todas as funcionalidades do plano {subscription.plan_detail.name} ao final do período.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowCancelConfirm(false)}
                        className="flex-1 py-2 px-4 border border-white/20 text-white rounded-lg hover:border-white/40"
                      >
                        Voltar
                      </button>
                      <button
                        onClick={() => {
                          onCancel();
                          setShowCancelConfirm(false);
                        }}
                        disabled={isLoading}
                        className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                      >
                        Confirmar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {subscription.status === 'cancelled' && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">
            Sua assinatura foi cancelada. Você continuará tendo acesso até o final do período pago.
          </p>
        </div>
      )}
    </div>
  );
}
